import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import Icon from '../../../../components/AppIcon';

interface User {
  id: string;
  name: string;
  fullName?: string;
  department?: string;
}

interface UserAssignmentPopoverProps {
  selectedUserIds: string[];
  onChange: (userIds: string[]) => void;
  allUsers: User[];
  departments: string[];
  disabled?: boolean;
}

const UserAssignmentPopover: React.FC<UserAssignmentPopoverProps> = ({
  selectedUserIds, onChange, allUsers, departments, disabled,
}) => {
  const { t } = useTranslation();
  const [dept, setDept] = useState('');
  const [search, setSearch] = useState('');

  const visibleUsers = useMemo(() => {
    let list = allUsers;
    if (dept) list = list.filter(u => (u.department || '') === dept);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(u => (u.fullName || u.name || '').toLowerCase().includes(q));
    }
    return list;
  }, [allUsers, dept, search]);

  const allVisibleSelected = visibleUsers.length > 0 && visibleUsers.every(u => selectedUserIds.includes(u.id));
  const someVisibleSelected = visibleUsers.some(u => selectedUserIds.includes(u.id));

  const toggleSelectAll = (checked: boolean) => {
    const visibleIds = new Set(visibleUsers.map(u => u.id));
    if (checked) {
      const merged = new Set([...selectedUserIds, ...visibleIds]);
      onChange(Array.from(merged));
    } else {
      onChange(selectedUserIds.filter(id => !visibleIds.has(id)));
    }
  };

  const toggleUser = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      onChange(selectedUserIds.filter(id => id !== userId));
    } else {
      onChange([...selectedUserIds, userId]);
    }
  };

  const selectedUsers = allUsers.filter(u => selectedUserIds.includes(u.id));

  const getInitials = (name: string) => {
    const parts = (name || '?').split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : (parts[0][0] || '?').toUpperCase();
  };

  return (
    <Popover.Root>
      <Popover.Trigger asChild disabled={disabled}>
        <button
          className="flex items-center gap-1.5 min-w-0"
          type="button"
          data-testid="user-assignment-trigger"
        >
          <div className="flex -space-x-2 rtl:space-x-reverse">
            {selectedUsers.slice(0, 3).map(u => (
              <div
                key={u.id}
                className="w-7 h-7 rounded-full bg-primary/10 text-primary text-[10px] font-medium flex items-center justify-center border-2 border-background"
                title={u.fullName || u.name}
              >
                {getInitials(u.fullName || u.name)}
              </div>
            ))}
          </div>
          {selectedUserIds.length > 3 && (
            <span className="text-xs font-medium text-muted-foreground">
              +{selectedUserIds.length - 3}
            </span>
          )}
          {selectedUserIds.length === 0 && (
            <span className="text-xs text-muted-foreground">{t('SelectUsers')}</span>
          )}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="w-80 bg-popover border border-border rounded-lg shadow-lg z-50 p-3 space-y-2"
          sideOffset={4}
          align="start"
        >
          <select
            value={dept}
            onChange={(e) => setDept(e.target.value)}
            className="w-full px-2 py-1.5 border border-border rounded-md text-sm bg-background"
            data-testid="user-popover-dept-filter"
          >
            <option value="">{t('All departments')}</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>

          <div className="relative">
            <Icon name="Search" size={14} className="absolute start-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('SearchUsers', { defaultValue: 'Search users...' })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full ps-7 pe-2 py-1.5 border border-border rounded-md text-sm bg-background"
              data-testid="user-popover-search"
            />
          </div>

          <label className="flex items-center gap-2 px-1 py-1 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
              onChange={(e) => toggleSelectAll(e.target.checked)}
              className="rounded border-border"
              data-testid="user-popover-select-all"
            />
            {t('SelectAllInDepartment', { defaultValue: 'Select all' })}
          </label>

          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {visibleUsers.map(u => (
              <label key={u.id} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedUserIds.includes(u.id)}
                  onChange={() => toggleUser(u.id)}
                  className="rounded border-border"
                />
                <span className="truncate">{u.fullName || u.name}</span>
                {u.department && (
                  <span className="text-xs text-muted-foreground ms-auto flex-shrink-0">{u.department}</span>
                )}
              </label>
            ))}
            {visibleUsers.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-2">{t('NoResults')}</p>
            )}
          </div>

          <div className="text-xs text-muted-foreground pt-1 border-t border-border">
            {t('UsersSelected', { count: selectedUserIds.length, defaultValue: `${selectedUserIds.length} users selected` })}
          </div>

          <Popover.Arrow className="fill-border" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default UserAssignmentPopover;
